function bufferToBase64url(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer.buffer || buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64urlToBuffer(value) {
  const input = String(value || '');
  const pad = '='.repeat((4 - (input.length % 4)) % 4);
  const b64 = (input + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out.buffer;
}

const PASSKEY_HINT_KEY = 'vurgun_passkey_registered';
const OFFER_KEY = 'vurgun_passkey_offer_dismissed';

export function isWebAuthnSupported() {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && typeof navigator.credentials?.create === 'function'
    && typeof navigator.credentials?.get === 'function'
    && window.isSecureContext;
}

export function hasLocalPasskeyHint() {
  try {
    return localStorage.getItem(PASSKEY_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markLocalPasskeyHint() {
  try {
    localStorage.setItem(PASSKEY_HINT_KEY, '1');
  } catch {}
}

export function clearLocalPasskeyHint() {
  try {
    localStorage.removeItem(PASSKEY_HINT_KEY);
  } catch {}
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  try {
    if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      return Boolean(await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
    }
  } catch {
    return false;
  }
  return true;
}

export async function shouldShowWebAuthnLogin() {
  if (hasLocalPasskeyHint()) return true;
  return isWebAuthnSupported();
}

function reviveCreationOptions(options) {
  const next = { ...options };
  next.challenge = base64urlToBuffer(options.challenge);
  if (options.user?.id) {
    next.user = {
      ...options.user,
      id: typeof options.user.id === 'string'
        ? base64urlToBuffer(options.user.id)
        : options.user.id,
    };
  }
  if (Array.isArray(options.excludeCredentials)) {
    next.excludeCredentials = options.excludeCredentials.map((item) => ({
      ...item,
      id: typeof item.id === 'string' ? base64urlToBuffer(item.id) : item.id,
    }));
  }
  return next;
}

function reviveRequestOptions(options) {
  const next = { ...options };
  next.challenge = base64urlToBuffer(options.challenge);
  if (Array.isArray(options.allowCredentials) && options.allowCredentials.length) {
    next.allowCredentials = options.allowCredentials.map((item) => ({
      ...item,
      id: typeof item.id === 'string' ? base64urlToBuffer(item.id) : item.id,
    }));
  } else {
    delete next.allowCredentials;
  }
  return next;
}

function serializeCredential(credential) {
  if (!credential) return null;
  const response = credential.response;
  const clientDataJSON = bufferToBase64url(response.clientDataJSON);
  const rawId = bufferToBase64url(credential.rawId);

  if (typeof response.attestationObject !== 'undefined') {
    return {
      id: credential.id,
      rawId,
      type: credential.type,
      clientExtensionResults: credential.getClientExtensionResults?.() || {},
      authenticatorAttachment: credential.authenticatorAttachment || undefined,
      response: {
        clientDataJSON,
        attestationObject: bufferToBase64url(response.attestationObject),
        transports: typeof response.getTransports === 'function' ? response.getTransports() : undefined,
      },
    };
  }

  return {
    id: credential.id,
    rawId,
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    response: {
      clientDataJSON,
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : undefined,
    },
  };
}

export async function createPlatformCredential(options) {
  const publicKey = reviveCreationOptions(options);
  const credential = await navigator.credentials.create({ publicKey });
  return serializeCredential(credential);
}

export async function getPlatformAssertion(options) {
  const publicKey = reviveRequestOptions(options);
  const credential = await navigator.credentials.get({
    publicKey,
    mediation: 'optional',
  });
  return serializeCredential(credential);
}

export function mapWebAuthnError(error, fallback) {
  const name = error?.name || '';
  const message = String(error?.message || '');
  if (name === 'NotAllowedError') return 'cancelled';
  if (name === 'InvalidStateError') return 'already_registered';
  if (name === 'NotSupportedError') return 'unsupported';
  if (name === 'SecurityError') return 'security';
  if (name === 'AbortError') return 'cancelled';
  if (/kayıtlı biyometrik|kayitli biyometrik|biyometrik giriş yok|biyometrik giris yok/i.test(message)) {
    return 'no_credentials';
  }
  return fallback || error?.message || 'error';
}

export function shouldOfferPasskeySave() {
  try {
    return localStorage.getItem(OFFER_KEY) !== '1';
  } catch {
    return true;
  }
}

export function dismissPasskeyOffer() {
  try {
    localStorage.setItem(OFFER_KEY, '1');
  } catch {}
}
